import type { ReactNode } from "react";

import { MuiAlert } from "@/components/ui/mui-alert";
import {
  MuiSnackbar,
  type MuiSnackbarOrigin,
  type MuiSnackbarProps,
} from "@/components/ui/mui-snackbar";

// react-ui's `Notification` (`timmy/packages/react-ui/src/notification/
// notification.tsx`), ported as a shared component: the donor's one toast,
// raised by the spec screens' `ErrorSnackbar`, by the communities list's
// hide/unhide results (`CommunityListIsland.tsx:660-670`), and by the spec
// pages' target-cost toast (`target-cost-notification.tsx`, #1216).
//
// It draws a **standard** `Alert` of the caller's severity with the severity's
// own icon and **no close X** — react-ui's `Alert` wires MUI's `onClose` only
// when `closable` is set (`alert/alert.tsx:58`), and no donor call site sets
// it (the community link errors carry it as a named break, #754) — with no
// width of its own, so as wide as its message. The timer is the only way out
// unless `closable`: the close handler returns early on `clickaway` and
// `escapeKeyDown` (`notification.tsx:62-64`). The clock still pauses under the
// pointer and while the window is away, as MUI's does.
//
// react-ui's `Snackbar` portals to the body (`snackbar.tsx:32`) and returns
// `null` the moment it is closed (`:25-27`): no shrink-out, only the grow in.

export interface NotificationProps {
  open: boolean;
  /** MUI's `severity`: the icon and the three colours. `warning` is the
   *  target-cost toast's (#1216). */
  severity: "success" | "error" | "warning";
  /** Milliseconds before it closes itself. Every donor call site names one,
   *  but the communities error snackbar's clock fires an `onClose` its call
   *  sites never pass, so it closes nothing: omitted, there is no clock (#754). */
  autoHideDuration?: number;
  /** MUI's `anchorOrigin`; `MuiSnackbar`'s bottom-centre when omitted. */
  anchorOrigin?: MuiSnackbarOrigin;
  /** react-ui's `anchorOffset` (`notification.tsx:85-88`): lengths that win
   *  over the origin's own offset — the link-delete toast's `top` below the
   *  app header (#754). */
  anchorOffset?: MuiSnackbarProps["anchorOffset"];
  /** react-ui's `closable`: MUI's close X on the alert, wired to `onClose`
   *  (`alert/alert.tsx:58`). Off by default — the donor sets it nowhere; the
   *  community link error snackbars carry it as a named break (#724 Q2). */
  closable?: boolean;
  /** The timer's close, and the X's when `closable`. */
  onClose: () => void;
  /** react-ui's `sx`, which it hands the **alert** — for a shared wrapper's
   *  own look (the communities error snackbar's weight and lift), never a
   *  call site's. */
  className?: string;
  /** The icon's colour over the severity's own — the target-cost toast's
   *  `'& .MuiAlert-icon': { color: 'inherit' }`, through react-ui's `sx`. */
  iconClassName?: string;
  children: ReactNode;
}

export function Notification({
  open,
  severity,
  autoHideDuration,
  anchorOrigin,
  anchorOffset,
  closable = false,
  onClose,
  className,
  iconClassName,
  children,
}: NotificationProps) {
  return (
    <MuiSnackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      closeOnEscape={false}
      portal
      instantExit
      anchorOrigin={anchorOrigin}
      anchorOffset={anchorOffset}
    >
      <MuiAlert
        severity={severity}
        onClose={closable ? onClose : undefined}
        className={className}
        iconClassName={iconClassName}
      >
        {children}
      </MuiAlert>
    </MuiSnackbar>
  );
}
